import { useParams } from 'react-router-dom'
import { PrintDeliveryProvider, usePrintDelivery } from '@/contexts/printDeliveryContext.tsx'
import { Badge, Checkbox, Collapse, Input, Loading, Select } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useEffect, useRef, useState } from 'react'
import { useClient } from '@/stores/useClient.ts'
import { IconCheck, IconExternalLink, IconFileTypePdf, IconTrash, IconTruckDelivery } from '@tabler/icons-react'
import PrintDelivery, { PrintDeliveryFile } from '@/models/printDelivery.ts'
import { captureException } from '@/controller/sentryHelper.ts'
import useCollapses from '@/hooks/useCollapses.tsx'
import { updateHelper } from '@/controller/updateHelper'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/stores/useApi.ts'
import DrawingTemplateRenderer from '@/components/drawing/DrawingTemplateRenderer.tsx'
import { templateOverridesFromDrive } from '@/models/drawing/drawingTypes.ts'
import DrawingEditor from '@/components/drawing/DrawingEditor.tsx'
import setters from '@/controller/setters.ts'
import { Trans, useTranslation } from 'react-i18next'
import useDrawingTemplates from '@/hooks/useDrawingTemplates.tsx'

export default function PrintDeliveryPage() {
  const { deliveryId } = useParams<{deliveryId: string}>()

  return <div className='flex w-full h-full flex-col'>
    <div className='w-full font-serif text-3xl bg-primary text-neutral'>
      <div className='container p-4 mx-auto'>
        FridaySnap
      </div>
    </div>
    <div className='container p-4 mx-auto'>
      <PrintDeliveryProvider deliveryId={deliveryId!}>
        <PrintDeliveryPageContentCheck />
      </PrintDeliveryProvider>
    </div>
  </div>
}

function PrintDeliveryPageContentCheck() {
  const { isLoading, printDelivery } = usePrintDelivery()
  const { t } = useTranslation()

  if (isLoading && !printDelivery) {
    return <Loading color='primary' />
  }

  if (!printDelivery) {
    return <p>{t('printDelivery.notFound')}</p>
  }

  return <PrintDeliveryPageContent />
}

function PrintDeliveryPageContent() {
  // const outlet = useOutlet()
  const api = useApi()
  const { printDelivery: printDeliveryOpt, updateDelivery } = usePrintDelivery()
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [isDeletingFile, setIsDeletingFile] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadPrintDeliveryFile, deletePrintDeliveryFile, updatePrintDelivery, confirmPrintDelivery } = useClient()
  const [isConfirming, setIsConfirming] = useState(false)
  const { collapse } = useCollapses()
  const printDelivery = printDeliveryOpt!
  const [printDeliveryData, setPrintDeliveryData] = useState(printDelivery)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedDrive, setSelectedDrive] = useState<string | null>(printDelivery.related_drives[0]?.id || null)
  const [selectedTemplate, setSelectedTemplate] = useState<null | string>(null)
  const [driveUrl, setDriveUrl] = useState('')
  const { t } = useTranslation()
  const drawingTemplates = useDrawingTemplates()


  useEffect(() => {
    // DriveURL looks like this /d/:driveId . e.g. http://localhost:5173/d/X63H8UfXFbpzhKctdQdJFN/cards or https://app.fridaysnap.de/d/CMfsfS4SM4vJHhHEerDHzh?_ha=TOKEN or https://app.fridaysnap.de/d/CMfsfS4SM4vJHhHEerDHzh
    const match = driveUrl.match(/\/d\/([A-Za-z0-9]+)/);
    if (match && match[1]) {
      setSelectedDrive(match[1]);
    }
  }, [driveUrl])

  const { data: fetchedDrive, isFetching: isFetchingDrive } = useQuery({
    queryKey: ['drive', selectedDrive],
    enabled: selectedDrive != null,
    queryFn: async () => {
      const res = await api.get(`api/drive/${selectedDrive}`)
      return res.data
    },
    staleTime: Infinity,
  })
  const templateOverrides = fetchedDrive && templateOverridesFromDrive(fetchedDrive)

  async function uploadFile(file: File) {
    setIsUploadingFile(true)
    try {
      const fileRes = await uploadPrintDeliveryFile(printDelivery.id, file)
      await updateDelivery({
        ...printDelivery,
        files: [
          ...printDelivery.files,
          fileRes.data,
        ]
      })
    } catch (e) {
      alert('Error: ' + JSON.stringify(e))
      captureException(e, 'uploading delivery file')
      console.error('error uploading new delivery file', e)
    } finally {
      setIsUploadingFile(false)
    }
  }

  async function onImageFileSelect(e: any) {
    if (isUploadingFile) {
      return
    }

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      await uploadFile(file)
    }
  }

  async function onFileDeleteClick(file: PrintDeliveryFile) {
    if (confirm(t('common.deleteRly'))) {
      setIsDeletingFile(true)
      try {
        await deletePrintDeliveryFile(file.id)
        await updateDelivery({
          ...printDelivery,
          files: [
            ...printDelivery.files.filter(existingFile => existingFile.id != file.id),
          ]
        })
      } catch (e) {
        alert('Error: ' + JSON.stringify(e))
        captureException(e, 'deleting delivery file')
      } finally {
        setIsDeletingFile(false)
      }
    }
  }

  function input(key: keyof PrintDelivery, name: string, disabled: boolean=true) {
    return <label className='form-control'>
      <div className='label'>
        <span className="label-text">{name}</span>
      </div>
      <Input disabled={disabled} value={printDeliveryData[key] as string} onChange={e => setPrintDeliveryData({...printDeliveryData, [key]: e.target.value})} />
    </label>
  }

  async function onDeliveryUpdateSubmit(e: any) {
    e.preventDefault()
    e.stopPropagation()

    await updateHelper(
      isSaving,
      setIsSaving,
      updatePrintDelivery,
      'Updating print delivery',
      t('printDelivery.errorUpdating'),
      async (res) => {
        setPrintDeliveryData(res)
        await updateDelivery(res)
      },
      printDelivery.id,
      {
        delivery_first_name: printDeliveryData.delivery_first_name,
        delivery_last_name: printDeliveryData.delivery_last_name,
        delivery_address: printDeliveryData.delivery_address,
        delivery_address_2: printDeliveryData.delivery_address_2,
        delivery_postal: printDeliveryData.delivery_postal,
        delivery_city: printDeliveryData.delivery_city,
        delivery_note: printDeliveryData.delivery_note,
      },
    )
  }

  async function onConfirmClick() {
    await updateHelper(
      isConfirming,
      setIsConfirming,
      confirmPrintDelivery,
      'Confirming print delivery',
      t('printDelivery.errorUpdating'),
      async (res) => {
        setPrintDeliveryData(res)
        await updateDelivery(res)
      },
      printDelivery.id,
    )

  }

  async function onSubmit(e: any) {
    e.preventDefault()
    e.stopPropagation()
    await onConfirmClick()
  }

  return <div className='w-full flex flex-col gap-3 py-5 max-'>
    <p>
      {t('printDelivery.forOrder')} <b>{printDelivery.order_id}</b>
    </p>
    <p className='flex flex-row gap-5'>
      <span>
        {t('printDelivery.format')} <b className='uppercase'>DIN {printDelivery.size}</b>
      </span>
      <span>
        {t('printDelivery.count')} <b className='uppercase'>{printDelivery.count}</b> {t('printDelivery.cards')}
      </span>
    </p>
    <Collapse className='collapse-arrow bg-secondary' open={collapse('address').open}>
      <Collapse.Title className='font-bold' onClick={collapse('address').onClick}>{t('printDelivery.checkDeliveryAddress')}</Collapse.Title>
      <Collapse.Content>
        <form className='flex flex-col gap-3 max-w-[20rem]' onSubmit={onDeliveryUpdateSubmit}>
          { input('delivery_first_name', t('printDelivery.firstName'), printDelivery.is_confirmed) }
          { input('delivery_last_name', t('printDelivery.lastName'), printDelivery.is_confirmed) }
          { input('delivery_address', t('printDelivery.address'), printDelivery.is_confirmed) }
          { input('delivery_address_2', t('printDelivery.addressExtra'), printDelivery.is_confirmed) }
          { input('delivery_postal', t('printDelivery.postalCode'), printDelivery.is_confirmed) }
          { input('delivery_city', t('printDelivery.city'), printDelivery.is_confirmed) }
          { input('delivery_note', t('printDelivery.optionalNote'), printDelivery.is_confirmed) }
          <p>{t('printDelivery.contactIfAddressWrong')}</p>
          { !printDelivery.is_confirmed && <Button loading={isSaving} disabled={isSaving} color='primary'>{t('common.save')}</Button> }
        </form>
      </Collapse.Content>
    </Collapse>
    <Collapse className='collapse-arrow bg-secondary' open={collapse('how').open}>
      <Collapse.Title className='font-bold' onClick={collapse('how').onClick}>{t('printDelivery.createPrintFile')}</Collapse.Title>
      <Collapse.Content>
        <h2 className='font-bold text-xl'>{t('printDelivery.instantCards')}</h2>
        <label className='flex flex-col gap-0 my-3'>
          <span className='text-sm'>
            {t('printDelivery.fridaysnapGallery')}
          </span>
         <div>
           {
             printDelivery.related_drives.length > 0 && selectedDrive && <>
             { printDelivery.related_drives.length > 1 && <Select value={selectedDrive} onChange={e => setSelectedDrive(e.currentTarget.value)}>
               { printDelivery.related_drives.map((d) => <Select.Option key={d.id} value={d.id}>{d.title}</Select.Option>)}
             </Select> }
             <p className='flex gap-1 items-center'>{t('printDelivery.isThisCorrectGallery')} <a className='text-primary underline inline-flex flex-row gap-1 items-center' target='_blank' href={`/d/${selectedDrive}`}><IconExternalLink /> {window.location.hostname}/d/{selectedDrive}</a></p>
             </>
           }
           {
             printDelivery.related_drives.length === 0 && <>
              <Input className='w-full' placeholder={t('printDelivery.enterGalleryLink')} {...setters(driveUrl, setDriveUrl)} />
             </>
           }
         </div>
        </label>
        <div className='max-w-[90vw] w-full'>
          { isFetchingDrive && <Loading color='primary' /> }
          { !isFetchingDrive && fetchedDrive && <>
            <div className='max-w-[90vw] flex flex-row overflow-scroll justify-start items-start'>
              { drawingTemplates.map(template =>
                <div key={template.id} className='w-[8rem] md:w-[12rem] h-[11.35rem] md:h-[17rem] p-2 cursor-pointer' onClick={() => setSelectedTemplate((template.id))}>
                  <DrawingTemplateRenderer template={template} elementOverrides={templateOverrides} />
                </div>
              ) }
            </div>
          </>}
        </div>
        { !isFetchingDrive && fetchedDrive && selectedTemplate &&
          <div className='h-[90vh]'>
            <p className='text-center'>{t('common.clickTextToAdapt')}</p>
            <DrawingEditor key={fetchedDrive.id + selectedTemplate} template={drawingTemplates.find(template => template.id == selectedTemplate)} elementOverrides={templateOverrides} toolsConfig={{
              hideDownload: true,
              toolButtons: [
                printDelivery.files.length == 0 ?
                {
                  element: <Button color='primary'>{t('printDelivery.createCardDesign')}</Button>,
                  onGetDataUrl: async (dataUrl) => {
                    collapse('how').onClick()
                    const res = await fetch(dataUrl)

                    return uploadFile(
                      new File(
                        [await res.blob()],
                        'card-design.jpg',
                        {
                          type: 'image/jpeg',
                          lastModified: Date.now(),
                        }
                      )
                    )
                  },
                } : {
                  element: <Badge color='primary'>{t('printDelivery.removeUploadedFileForNew')}</Badge>
                },
              ]
            }} />
          </div>
        }
        <p className='italic'>{t('printDelivery.or')}</p>
        <h2 className='font-bold text-xl'>{t('printDelivery.customizableCards')}</h2>
        <p>
          <Trans
            i18nKey='printDelivery.canvaTemplatesDesc'
            values={{
              weddingTemplates: t('printDelivery.canvaTemplatesWedding'),
              birthdayTemplates: t('printDelivery.canvaTemplatesBirthday')
            }}
          >
            <b><a className='underline text-primary' target='_blank' href="https://fridaysnap.de/kostenlose-kartendesign-vorlagen-fuer-qr-code-hochzeitsfotos/" /></b>
            <b><a target='_blank' className='text-primary underline' href="https://fridaysnap.de/kostenlose-kartendesign-vorlagen-fuer-qr-code-geburtstags-fotos/" /></b>
          </Trans>
        </p><br/>
        <p>{t('printDelivery.canvaExportInstructions')}</p>
      </Collapse.Content>
    </Collapse>
    { printDelivery.files.length == 0 && <>
      <p></p>
      <input className='hidden' ref={fileInputRef} type='file' accept='image/jpeg,image/png,application/pdf' onChange={onImageFileSelect} />
      <div>
        <Button loading={isUploadingFile} disabled={isUploadingFile} onClick={() => fileInputRef.current!.click()} color='primary'>{t('printDelivery.uploadPrintFile')}</Button>
        <p className='mt-3'>{t('printDelivery.allowedFormats')}</p>
      </div>
    </>}
    {
      printDelivery.files.length > 0 && <>
        <h2 className='font-bold mt-5'>{t('printDelivery.printFile')}</h2>
        { !printDelivery.is_confirmed && <p><span className='inline-flex justify-center items-center font-bold rounded-full bg-primary w-[1.5rem] aspect-square text-neutral me-1 '>!</span> {t('printDelivery.mustConfirmUpload')}</p>}
        { printDelivery.files.map(file => <div key={file.id} className='flex flex-row items-center w-full max-w-full'>
          <a className='text-primary underline flex flex-row items-center text overflow-hidden flex-grow min-w-0' href={file.file} target='_blank'>
            { file.file_name.toLowerCase().endsWith('pdf') && <IconFileTypePdf size={40} className='me-3 flex-shrink-0' />}
            { (file.file_name.toLowerCase().endsWith('png') || file.file_name.toLowerCase().endsWith('jpg') || file.file_name.toLowerCase().endsWith('jpeg')) && <img src={file.file} className='w-[6rem] shadow-lg me-3 flex-shrink-0' />}
            <span className="truncate">{file.file_name}</span>
          </a>
          { !printDelivery.is_confirmed && <Button loading={isDeletingFile} disabled={isDeletingFile} className='ms-auto' color='ghost' onClick={() => onFileDeleteClick(file)}>
            <IconTrash size={24} />
          </Button> }
        </div>)}
        { !printDelivery.is_confirmed && <form onSubmit={onSubmit} className='flex flex-col gap-3 mt-3'>
          <label className='flex flex-row gap-2'>
            <Checkbox color='primary' required />
            {t('printDelivery.checkedAddressAndQr')}
          </label>
          <div>
            <Button color='primary'><IconCheck /> {t('printDelivery.submitToPrint')}</Button>
          </div>
          <p className='mt-3'>{t('printDelivery.weWillCheckAgain')}</p>
        </form> }
      </>
    }
    {
      printDelivery.is_sent_to_print && <div className='flex flex-col gap-3 mt-5'>
        <div className='bg-success rounded-full flex justify-center items-center text-neutral font-bold py-2 px-5 gap-3'>
          <IconTruckDelivery size={30} />
          {t('printDelivery.sentToPrint')}
        </div>
      </div>
    }
    {
      printDelivery.is_confirmed && <div className='flex flex-col gap-3 mt-5'>
      <div className='bg-success rounded-full flex justify-center items-center text-neutral font-bold py-2 px-5 gap-3'>
        <IconCheck size={30} />
        {t('printDelivery.successfullySubmitted')}
      </div>
        <p>{t('printDelivery.thankYouForSubmitting')}</p>
        <p>{t('printDelivery.deliveryTimeframe', {from: 3, to: 5})}</p>
      </div>
    }
  </div>
}
